import React, { useEffect, useState, useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, FileText, Key, Search, Brain, Settings, ChevronLeft, ChevronRight, ChevronDown,
  MessageSquare, Megaphone, GraduationCap, Share2, Mail, Target, Image, BookOpen, TrendingUp,
  Calendar, DollarSign, BarChart3, Users, Zap, Bot, Lock, Globe, Send, FileCheck, Shield, Link2,
  MapPin, Palette, Cookie, LineChart, Network,
  Sparkles, Library, RotateCcw, Tv, Lightbulb, GitMerge, Facebook, Activity, Radar, ClipboardList,
  Package, ShoppingBag
} from 'lucide-react';
import useUiStore from '../../stores/uiStore';
import useAuthStore, { hasRole } from '../../stores/authStore';
import useModuleStore from '../../stores/moduleStore';
import Logo from '../shared/Logo';

// Icon-Mapping (String → Component)
const ICONS = {
  Globe, Target, FileText, Image, BookOpen, Brain, Search, TrendingUp, Calendar,
  Megaphone, DollarSign, Share2, Mail, GraduationCap, Users, BarChart3, Zap, Bot,
  Key, MessageSquare, LayoutDashboard, Lock, Send, FileCheck, Link2, MapPin, Palette,
  Cookie, LineChart, Network,
  Sparkles, Library, RotateCcw, Tv, Lightbulb, GitMerge, Facebook, Activity, Radar,
  ClipboardList, Package, ShoppingBag,
};

// ── Navigation-Struktur (modular) ─────────────────────────────
// comingSoon: true → Seite existiert noch nicht, zeigt "Kommt bald" statt 404
// Export: wird von CommandPalette wiederverwendet (Single-Source-of-Truth).
export const NAV_GROUPS = [
  { key: 'dashboard', label: 'Dashboard', path: '/', icon: 'LayoutDashboard' },
  {
    key: 'goals', label: 'Ziele', icon: 'Target', collapsible: true,
    items: [
      { key: 'campaigns', label: 'Alle Ziele', path: '/campaigns', icon: 'Target' }
    ]
  },
  {
    key: 'website', label: 'Website', icon: 'Globe', collapsible: true,
    items: [
      { key: 'pages', label: 'Seiten', module: 'website', path: '/content?tab=seiten', icon: 'FileText' },
      { key: 'landing', label: 'Landing Pages', module: 'landing_pages', path: '/content?tab=landing', icon: 'Target' },
      { key: 'blog', label: 'Blog', module: 'blog', path: '/content?tab=blog', icon: 'BookOpen' },
      { key: 'media', label: 'Medien', module: 'media', path: '/content?tab=medien', icon: 'Image' },
      { key: 'links', label: 'Verlinkung', module: 'website', path: '/links', icon: 'Link2' },
      { key: 'cookie-banner', label: 'Cookie-Banner', module: 'website', path: '/cookie-banner', icon: 'Cookie' },
    ]
  },
  { key: 'dna', label: 'DNA', module: 'dna', path: '/dna', icon: 'Brain' },
  { key: 'brand', label: 'Brand-Hub', path: '/brand', icon: 'Palette' },
  {
    key: 'marketing', label: 'Marketing', icon: 'TrendingUp', collapsible: true,
    items: [
      { key: 'content-package', label: 'Content-Paket', path: '/content-package', icon: 'Zap' },
      // Planung — Content-Kalender + Strategy-Dashboard (Aging, Cannibalization, Authority)
      {
        key: 'marketing-planning-group',
        label: 'Planung',
        icon: 'Calendar',
        collapsible: true,
        items: [
          { key: 'content-plan', label: 'Content-Plan', module: 'content_strategy', path: '/content-plan', icon: 'FileText' },
          { key: 'strategy', label: 'Strategie', module: 'content_strategy', path: '/content-strategy', icon: 'TrendingUp' },
        ],
      },
      // Keywords — Pool + Cluster-Map
      {
        key: 'marketing-keywords-group',
        label: 'Keywords',
        icon: 'Key',
        collapsible: true,
        items: [
          { key: 'keywords', label: 'Keyword-Pool', module: 'keywords', path: '/keywords', icon: 'Key' },
          { key: 'clusters', label: 'Cluster-Map', module: 'keywords', path: '/clusters', icon: 'Network' },
        ],
      },
      // SEO & Auffindbarkeit — klassisches SEO + AEO (Antwort-Engines)
      {
        key: 'marketing-seo-group',
        label: 'SEO & Auffindbarkeit',
        icon: 'Search',
        collapsible: true,
        items: [
          { key: 'seo', label: 'SEO', module: 'seo', path: '/seo', icon: 'Search' },
          { key: 'aeo', label: 'AEO (Antwort-Engines)', module: 'aeo', path: '/aeo', icon: 'Bot' },
        ],
      },
      { key: 'listings', label: 'Listings', module: 'listings', path: '/listings', icon: 'MapPin' },
    ]
  },
  {
    key: 'ads', label: 'Werbung', icon: 'Megaphone', collapsible: true,
    items: [
      // Wizard prominent oben — Klaus' erste Aktion
      { key: 'ads-wizard', label: '+ Neue Kampagne', module: 'ads_base', path: '/ads/wizard/beginner', icon: 'Sparkles', highlight: true },
      // Cross-Channel-Übersicht (alle Plattformen aggregiert)
      { key: 'cross-channel', label: 'Cross-Channel', module: 'cross_channel', path: '/cross-channel', icon: 'GitMerge' },
      // Living Campaign Engine
      { key: 'living-campaigns', label: 'Living Campaign Engine', path: '/living-campaigns', icon: 'Zap' },
      // Google Ads — eigene Sub-Gruppe symmetrisch zu Meta
      {
        key: 'ads-google-group',
        label: 'Google Ads',
        icon: 'DollarSign',
        module: 'ads_google',
        collapsible: true,
        items: [
          { key: 'ads-google-overview', label: 'Übersicht', module: 'ads_google', path: '/ads?tab=overview', icon: 'BarChart3' },
          { key: 'ads-google-campaigns', label: 'Kampagnen', module: 'ads_google', path: '/ads?tab=campaigns', icon: 'Megaphone' },
          { key: 'ads-google-search-terms', label: 'Search-Terms', module: 'ads_google', path: '/ads?tab=search-terms', icon: 'Search' },
          { key: 'ads-google-assets', label: 'Asset-Studio', module: 'ads_google', path: '/ads?tab=assets', icon: 'Library' },
          { key: 'ads-google-conversions', label: 'Conversions', module: 'ads_google', path: '/ads?tab=conversions', icon: 'Target' },
          { key: 'ads-google-closed-loop', label: 'Closed-Loop', module: 'ads_google', path: '/ads?tab=closed-loop', icon: 'RotateCcw' },
          { key: 'ads-google-recommendations', label: 'Empfehlungen', module: 'ads_google', path: '/ads?tab=recommendations', icon: 'Lightbulb' },
          { key: 'ads-google-pmax', label: 'Performance Max', module: 'ads_google', path: '/ads?tab=pmax', icon: 'Zap' },
          { key: 'ads-google-demand-gen', label: 'Demand Gen', module: 'ads_google', path: '/ads?tab=demand-gen', icon: 'Tv' },
        ],
      },
      // Meta Ads — bestehende Sub-Gruppe
      {
        key: 'ads-meta-group',
        label: 'Meta Ads',
        icon: 'Facebook',
        collapsible: true,
        items: [
          { key: 'ads-meta', label: 'Kampagnen', module: 'ads_meta', path: '/meta-ads', icon: 'Facebook' },
          { key: 'ads-meta-insights', label: 'Insights', module: 'ads_meta', path: '/meta-ads/insights', icon: 'BarChart3' },
          { key: 'ads-meta-radar', label: 'Ad Radar', module: 'ads_meta', path: '/meta-ads/radar', icon: 'Radar' },
          { key: 'ads-meta-audiences', label: 'Zielgruppen', module: 'ads_meta', path: '/meta-ads/audiences', icon: 'Users' },
          { key: 'ads-meta-pixel', label: 'Pixel & CAPI', module: 'ads_meta', path: '/meta-ads/pixel', icon: 'Activity' },
          { key: 'ads-meta-leadforms', label: 'Lead-Forms', module: 'ads_meta', path: '/meta-ads/leadforms', icon: 'ClipboardList' },
          { key: 'ads-meta-rules', label: 'Automation', module: 'ads_meta', path: '/meta-ads/rules', icon: 'Zap' },
          { key: 'ads-meta-catalog', label: 'Produktkatalog', module: 'ads_meta', path: '/meta-ads/catalog', icon: 'Package' },
          { key: 'ads-meta-messaging', label: 'Messaging-Ads', module: 'ads_meta', path: '/meta-ads/messaging', icon: 'MessageSquare' },
          { key: 'ads-meta-ig-shopping', label: 'IG Shopping', module: 'ads_meta', path: '/meta-ads/instagram-shopping', icon: 'ShoppingBag' },
        ],
      },
      { key: 'ads-linkedin', label: 'LinkedIn Ads', module: 'ads_linkedin', path: '/ads', icon: 'DollarSign', comingSoon: true },
      { key: 'multi-channel', label: 'Multi-Channel-Push', path: '/multi-channel', icon: 'Network' },
    ]
  },
  {
    key: 'channels', label: 'Kanäle', icon: 'Share2', collapsible: true,
    items: [
      { key: 'social', label: 'Social Media', module: 'social_media', path: '/social-media', icon: 'Share2' },
      { key: 'newsletter', label: 'Newsletter', module: 'newsletter', path: '/newsletter', icon: 'Mail' },
    ]
  },
  { key: 'academy', label: 'Academy', module: 'academy', path: '/academy', icon: 'GraduationCap' },
  { key: 'crm', label: 'Anfragen', module: 'crm', path: '/contact-submissions', icon: 'Users' },
  { key: 'analytics', label: 'Analytics', module: 'analytics', path: '/analytics', icon: 'LineChart' },
  { key: 'reporting', label: 'Reporting', module: 'reporting', path: '/reporting', icon: 'BarChart3', comingSoon: true },
  { key: 'automations', label: 'Automations', module: 'automations', path: '/automations', icon: 'Zap', comingSoon: true },
  { key: 'agent', label: 'KI-Agent', module: 'agent', path: '/agent', icon: 'Bot', comingSoon: true },
];

// ── Upsell-Dialog ─────────────────────────────────────────────
function UpsellDialog({ label, onClose }) {
  const { user } = useAuthStore();
  const isAdmin = user && hasRole(user.role, 'admin');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-6 max-w-sm mx-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
            <Lock size={20} className="text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white">{label}</h3>
            <p className="text-sm text-gray-500 dark:text-slate-400">Modul nicht aktiviert</p>
          </div>
        </div>
        <p className="text-sm text-gray-600 dark:text-slate-300 mb-5">
          {isAdmin
            ? `Aktiviere "${label}" in den Einstellungen unter Module.`
            : `Kontaktiere deinen Administrator um "${label}" freizuschalten.`
          }
        </p>
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 px-4 py-2 text-sm text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
            Schließen
          </button>
          {isAdmin && (
            <a href="/settings?tab=modules" className="flex-1 px-4 py-2 text-sm text-white bg-primary-600 hover:bg-primary-700 rounded-lg text-center transition-colors">
              Zu den Modulen
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

// ── NavItem (einzeln) ─────────────────────────────────────────
function NavItem({ path, label, icon: iconKey, collapsed, indent, module: moduleKey, disabled, onLocked, comingSoon }) {
  const Icon = ICONS[iconKey] || FileText;

  // Hooks MUESSEN vor allen conditionals stehen (Rules of Hooks)
  const location = useLocation();
  const isActive = useMemo(() => {
    if (!path) return false;
    if (path === '/') return location.pathname === '/';
    if (path.includes('?')) {
      const [basePath, queryStr] = path.split('?');
      if (location.pathname !== basePath) return false;
      const params = new URLSearchParams(queryStr);
      const currentParams = new URLSearchParams(location.search);
      for (const [key, val] of params) {
        if (currentParams.get(key) !== val) return false;
      }
      return true;
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  }, [path, location.pathname, location.search]);

  // Kommt bald — ausgegraut, kein Link, Tooltip
  if (comingSoon && !disabled) {
    return (
      <div
        className={`group relative flex items-center ${collapsed ? 'justify-center' : `gap-3 ${indent ? 'pl-9 pr-3' : 'px-3'}`} py-2.5 rounded-xl text-sm font-medium text-gray-400 dark:text-slate-600 cursor-default`}
        title="Kommt bald"
      >
        <Icon size={collapsed ? 20 : 18} className="flex-shrink-0 opacity-50" />
        {!collapsed && (
          <>
            <span className="flex-1 opacity-50">{label}</span>
            <span className="text-[9px] font-semibold text-gray-400 dark:text-slate-600 bg-gray-100 dark:bg-slate-800 rounded px-1.5 py-0.5">BALD</span>
          </>
        )}
        {collapsed && (
          <div className="absolute left-full ml-2 px-2.5 py-1 bg-gray-800 dark:bg-slate-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap z-50 shadow-xl">
            {label} — Kommt bald
          </div>
        )}
      </div>
    );
  }

  if (disabled) {
    return (
      <button
        onClick={() => onLocked?.(label)}
        className={`group relative w-full flex items-center ${collapsed ? 'justify-center' : `gap-3 ${indent ? 'pl-9 pr-3' : 'px-3'}`} py-2.5 rounded-xl text-sm font-medium transition-all duration-200 text-gray-400 dark:text-slate-600 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-800/30`}
      >
        <Icon size={collapsed ? 20 : 18} className="flex-shrink-0 opacity-50" />
        {!collapsed && (
          <>
            <span className="flex-1 opacity-50">{label}</span>
            <Lock size={12} className="opacity-40" />
          </>
        )}
        {collapsed && (
          <div className="absolute left-full ml-2 px-2.5 py-1 bg-gray-800 dark:bg-slate-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap z-50 shadow-xl flex items-center gap-1.5">
            <Lock size={10} /> {label}
          </div>
        )}
      </button>
    );
  }

  return (
    <NavLink
      to={path}
      end={path === '/'}
      className={
        `group relative flex items-center ${collapsed ? 'justify-center' : `gap-3 ${indent ? 'pl-9 pr-3' : 'px-3'}`} py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
          isActive
            ? 'bg-primary-50 dark:bg-slate-800 text-primary-700 dark:text-white shadow-sm dark:shadow-lg dark:shadow-slate-900/50'
            : 'text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-800/50 hover:text-gray-900 dark:hover:text-slate-200'
        }`
      }
    >
      {isActive && (
        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 rounded-r-full bg-primary-500" />
      )}
      <Icon size={collapsed ? 20 : 18} className={`flex-shrink-0 transition-colors ${isActive ? 'text-primary-600 dark:text-primary-400' : 'group-hover:text-primary-600 dark:group-hover:text-primary-400'}`} />
      {!collapsed && <span>{label}</span>}
      {collapsed && (
        <div className="absolute left-full ml-2 px-2.5 py-1 bg-gray-800 dark:bg-slate-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap z-50 shadow-xl">
          {label}
        </div>
      )}
    </NavLink>
  );
}

// ── NavGroup (aufklappbar) ────────────────────────────────────
function NavGroup({ item, collapsed, isEnabled, onLocked }) {
  const location = useLocation();
  const { sidebarGroups, toggleSidebarGroup } = useUiStore();
  const expanded = sidebarGroups?.[item.key] ?? true;
  const Icon = ICONS[item.icon] || FileText;

  // Pruefen ob mind. 1 Child aktiv (enabled) ist
  const visibleChildren = item.items || [];
  const allDisabled = visibleChildren.every(c => c.module && !isEnabled(c.module));

  const isChildActive = visibleChildren.some(child => {
    if (!child.path) return false;
    if (child.path === '/') return location.pathname === '/';
    if (child.path.includes('?')) {
      const [basePath, queryStr] = child.path.split('?');
      if (location.pathname !== basePath) return false;
      const params = new URLSearchParams(queryStr);
      const currentParams = new URLSearchParams(location.search);
      for (const [key, val] of params) {
        if (currentParams.get(key) !== val) return false;
      }
      return true;
    }
    return location.pathname === child.path || location.pathname.startsWith(child.path + '/');
  });

  if (collapsed) {
    return (
      <div className="relative group">
        <button
          className={`w-full flex items-center justify-center py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
            allDisabled
              ? 'text-gray-400 dark:text-slate-600'
              : isChildActive
                ? 'bg-primary-50 dark:bg-slate-800 text-primary-700 dark:text-white'
                : 'text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-800/50'
          }`}
        >
          <Icon size={20} className={isChildActive ? 'text-primary-600 dark:text-primary-400' : allDisabled ? 'opacity-50' : 'group-hover:text-primary-600 dark:group-hover:text-primary-400'} />
        </button>
        <div className="absolute left-full top-0 ml-2 py-2 px-1 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl shadow-xl opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto transition-opacity z-50 min-w-[180px]">
          <div className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wider">{item.label}</div>
          {visibleChildren.flatMap(child => {
            // Nested Sub-Group: flach einbauen mit einem "Gruppen-Label" davor
            if (child.collapsible && Array.isArray(child.items)) {
              return [
                <div key={`${child.key}-label`} className="mt-1.5 px-3 py-1 text-[10px] font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wider border-t border-gray-100 dark:border-slate-700 pt-2">
                  {child.label}
                </div>,
                ...child.items,
              ];
            }
            return [child];
          }).map(child => {
            // Gruppen-Labels (plain div) direkt durchlassen
            if (child && child.$$typeof) return child;
            const ChildIcon = ICONS[child.icon] || FileText;
            const childDisabled = child.module && !isEnabled(child.module);
            if (childDisabled) {
              return (
                <button
                  key={child.key}
                  onClick={() => onLocked?.(child.label)}
                  className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-gray-400 dark:text-slate-600 hover:bg-gray-50 dark:hover:bg-slate-700/30"
                >
                  <ChildIcon size={15} className="opacity-50" />
                  <span className="opacity-50">{child.label}</span>
                  <Lock size={10} className="ml-auto opacity-40" />
                </button>
              );
            }
            if (child.comingSoon) {
              return (
                <div key={child.key} className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-gray-400 dark:text-slate-600">
                  <ChildIcon size={15} className="opacity-50" />
                  <span className="opacity-50">{child.label}</span>
                  <span className="text-[9px] font-semibold bg-gray-100 dark:bg-slate-700 rounded px-1 ml-auto">BALD</span>
                </div>
              );
            }
            const childActive = (() => {
              if (!child.path) return false;
              if (child.path.includes('?')) {
                const [bp, qs] = child.path.split('?');
                if (location.pathname !== bp) return false;
                const p = new URLSearchParams(qs);
                const cp = new URLSearchParams(location.search);
                for (const [k, v] of p) { if (cp.get(k) !== v) return false; }
                return true;
              }
              return location.pathname === child.path || location.pathname.startsWith(child.path + '/');
            })();
            return (
              <NavLink
                key={child.key}
                to={child.path}
                className={
                  `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                    childActive
                      ? 'bg-primary-50 dark:bg-slate-700 text-primary-700 dark:text-white'
                      : 'text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700/50'
                  }`
                }
              >
                <ChildIcon size={15} />
                <span>{child.label}</span>
              </NavLink>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div>
      <button
        onClick={() => toggleSidebarGroup(item.key)}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
          allDisabled
            ? 'text-gray-400 dark:text-slate-600'
            : isChildActive
              ? 'text-primary-700 dark:text-white'
              : 'text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-800/50 hover:text-gray-900 dark:hover:text-slate-200'
        }`}
      >
        <Icon size={18} className={isChildActive ? 'text-primary-600 dark:text-primary-400' : allDisabled ? 'opacity-50' : ''} />
        <span className={`flex-1 text-left ${allDisabled ? 'opacity-50' : ''}`}>{item.label}</span>
        {allDisabled && <Lock size={12} className="opacity-40 mr-1" />}
        <ChevronDown size={14} className={`text-gray-400 dark:text-slate-500 transition-transform duration-200 ${expanded ? '' : '-rotate-90'}`} />
      </button>
      {expanded && (
        <div className="mt-0.5 space-y-0.5">
          {visibleChildren.map(child => {
            // Nested Sub-Group (z.B. "Meta Ads" innerhalb "Werbung")
            if (child.collapsible && Array.isArray(child.items)) {
              return (
                <NestedNavGroup
                  key={child.key}
                  item={child}
                  isEnabled={isEnabled}
                  onLocked={onLocked}
                />
              );
            }
            return (
              <NavItem
                key={child.key}
                {...child}
                collapsed={false}
                indent
                disabled={child.module ? !isEnabled(child.module) : false}
                onLocked={onLocked}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── NestedNavGroup — Sub-Gruppe innerhalb einer Top-Level-Gruppe ─────────────
// Wird z.B. fuer "Meta Ads" innerhalb "Werbung" genutzt. Eine weitere Ebene
// collapsible. Kein nested-in-collapsed-Rendering (nur im expanded Sidebar
// relevant — collapsed zeigt sowieso nur Icons).
function NestedNavGroup({ item, isEnabled, onLocked }) {
  const location = useLocation();
  const { sidebarGroups, toggleSidebarGroup } = useUiStore();
  const expanded = sidebarGroups?.[item.key] ?? false; // Sub-Groups default zu: zugeklappt
  const Icon = ICONS[item.icon] || FileText;

  const visibleChildren = item.items || [];
  const allDisabled = visibleChildren.every(c => c.module && !isEnabled(c.module));
  const isChildActive = visibleChildren.some(child => {
    if (!child.path) return false;
    if (child.path === '/') return location.pathname === '/';
    return location.pathname === child.path || location.pathname.startsWith(child.path + '/');
  });

  return (
    <div>
      <button
        onClick={() => toggleSidebarGroup(item.key)}
        className={`w-full flex items-center gap-2.5 pl-6 pr-3 py-2 rounded-lg text-sm transition-all duration-200 ${
          allDisabled
            ? 'text-gray-400 dark:text-slate-600'
            : isChildActive
              ? 'text-primary-700 dark:text-primary-400 font-medium'
              : 'text-gray-600 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-800/50'
        }`}
      >
        <Icon size={15} className={isChildActive ? 'text-primary-600 dark:text-primary-400' : ''} />
        <span className={`flex-1 text-left ${allDisabled ? 'opacity-50' : ''}`}>{item.label}</span>
        {allDisabled && <Lock size={10} className="opacity-40 mr-1" />}
        <ChevronDown size={12} className={`text-gray-400 dark:text-slate-500 transition-transform duration-200 ${expanded ? '' : '-rotate-90'}`} />
      </button>
      {expanded && (
        <div className="mt-0.5 ml-3 space-y-0.5 border-l border-gray-200 dark:border-slate-700/50 pl-1">
          {visibleChildren.map(child => (
            <NavItem
              key={child.key}
              {...child}
              collapsed={false}
              indent
              disabled={child.module ? !isEnabled(child.module) : false}
              onLocked={onLocked}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Sidebar (Hauptkomponente) ─────────────────────────────────
export default function Sidebar() {
  const { sidebarOpen, sidebarCollapsed, toggleSidebarCollapse } = useUiStore();
  const { user } = useAuthStore();
  const { fetchModules, isEnabled, loaded } = useModuleStore();
  const [upsellLabel, setUpsellLabel] = useState(null);
  const collapsed = sidebarCollapsed;
  const w = collapsed ? 'w-[68px]' : 'w-60';

  // Module laden beim ersten Render
  useEffect(() => {
    if (!loaded) fetchModules();
  }, [loaded, fetchModules]);

  if (!sidebarOpen) return null;

  return (
    <aside className={`fixed left-0 top-0 h-full ${w} bg-white dark:bg-slate-900 border-r border-gray-200 dark:border-slate-700/50 z-30 flex flex-col transition-all duration-300 ease-in-out`}>
      {/* Logo */}
      <div className={`flex items-center ${collapsed ? 'justify-center px-2' : 'px-5'} py-5 border-b border-gray-200 dark:border-slate-700/50`}>
        {collapsed ? (
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-white font-bold text-sm">A</div>
        ) : (
          <Logo size="md" variant="light" />
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
        {NAV_GROUPS.map((item) => {
          if (item.collapsible && item.items) {
            return (
              <NavGroup
                key={item.key}
                item={item}
                collapsed={collapsed}
                isEnabled={isEnabled}
                onLocked={setUpsellLabel}
              />
            );
          }
          return (
            <NavItem
              key={item.key}
              {...item}
              collapsed={collapsed}
              disabled={item.module ? !isEnabled(item.module) : false}
              onLocked={setUpsellLabel}
            />
          );
        })}
      </nav>

      {/* Bottom Section */}
      <div className="border-t border-gray-200 dark:border-slate-700/50 px-2 py-3 space-y-1">
        {/* System — nur Admin/Owner */}
        {user && hasRole(user.role, 'admin') && (
          <NavLink
            to="/system"
            className={({ isActive }) =>
              `group relative flex items-center ${collapsed ? 'justify-center' : 'gap-3 px-3'} py-2 rounded-xl text-sm transition-all duration-200 ${
                isActive
                  ? 'bg-primary-50 dark:bg-slate-800 text-primary-700 dark:text-white'
                  : 'text-gray-500 dark:text-slate-500 hover:bg-gray-100 dark:hover:bg-slate-800/50 hover:text-gray-700 dark:hover:text-slate-300'
              }`
            }
          >
            <Shield size={collapsed ? 18 : 16} />
            {!collapsed && <span>System</span>}
            {collapsed && (
              <div className="absolute left-full ml-2 px-2.5 py-1 bg-slate-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap z-50">
                System
              </div>
            )}
          </NavLink>
        )}

        <NavLink
          to="/settings"
          className={({ isActive }) =>
            `group relative flex items-center ${collapsed ? 'justify-center' : 'gap-3 px-3'} py-2 rounded-xl text-sm transition-all duration-200 ${
              isActive
                ? 'bg-primary-50 dark:bg-slate-800 text-primary-700 dark:text-white'
                : 'text-gray-500 dark:text-slate-500 hover:bg-gray-100 dark:hover:bg-slate-800/50 hover:text-gray-700 dark:hover:text-slate-300'
            }`
          }
        >
          <Settings size={collapsed ? 18 : 16} />
          {!collapsed && <span>Einstellungen</span>}
          {collapsed && (
            <div className="absolute left-full ml-2 px-2.5 py-1 bg-slate-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap z-50">
              Einstellungen
            </div>
          )}
        </NavLink>

        {!collapsed && user && (
          <div className="flex items-center gap-3 px-3 py-2 mt-1">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-teal-600 flex items-center justify-center text-white text-xs font-bold">
              {(user.name || user.email || '?').charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium text-gray-700 dark:text-slate-300 truncate">{user.name || user.email}</div>
              <div className="text-[10px] text-gray-500 dark:text-slate-500 truncate">{user.role || 'User'}</div>
            </div>
          </div>
        )}

        <button
          onClick={() => toggleSidebarCollapse?.()}
          className="w-full flex items-center justify-center py-1.5 text-gray-400 dark:text-slate-600 hover:text-gray-600 dark:hover:text-slate-400 transition-colors"
          title={collapsed ? 'Sidebar erweitern' : 'Sidebar einklappen'}
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </div>

      {/* Upsell-Dialog */}
      {upsellLabel && (
        <UpsellDialog label={upsellLabel} onClose={() => setUpsellLabel(null)} />
      )}
    </aside>
  );
}
